/**
 * Masterclass progress, on the account rather than in the browser.
 *
 * It used to live only in localStorage. Two consequences, both bad: a coach
 * who read half a masterclass on their laptop opened their phone to a blank
 * one, and nobody at BAM could see who was reading anything. Reading a
 * masterclass is the clearest "this is working for me" signal a new member
 * gives off, and it was the one signal the platform threw away.
 *
 * Two different jobs, two existing systems, no new tables:
 *
 *   user_data         the coach's own state, so progress follows them. A
 *                     generic key/value table with upsert on (user_id, key),
 *                     already used for written answers and saved plans.
 *   analytics_events  the aggregate view, via logEvent. Already wired, and
 *                     already written with service_role server-side, so it
 *                     works regardless of the browser's auth state.
 *
 * Reconciliation is a UNION, never a replace. Both sides are "pages this
 * person has read", a claim that only ever grows, so merging can lose
 * nothing. A last-write-wins sync would let a phone that loaded stale data
 * erase a chapter read on the laptop ten seconds earlier.
 */
import { useEffect, useRef } from "react";
import { loadUserData, saveUserData } from "../utils/userData";
import { logEvent } from "../utils/analytics";

const keyFor = (moduleKey) => `mc_visited_${moduleKey}`;
const SAVE_DEBOUNCE_MS = 900;

/* One small index across every masterclass: { [moduleKey]: { read, total,
   at } }. The per-module maps above say WHICH pages, and answering "what
   is half finished" from them means one read per masterclass plus knowing
   every module's length, which the home page does not load. The index is
   the answer to that one question, kept in this browser for an instant
   read and on the account so it follows the coach. */
export const INDEX_KEY = "mc_progress";
const INDEX_LOCAL = "bam_mc_progress";

export function readProgressIndex() {
  try {
    const v = JSON.parse(localStorage.getItem(INDEX_LOCAL) || "{}");
    return v && typeof v === "object" && !Array.isArray(v) ? v : {};
  } catch { return {}; }
}

/* Which masterclasses this browser has opened at all, so "new masterclass"
   stops being offered the moment somebody opens it, before they have
   finished a single page. Local only: the progress index above covers the
   other devices as soon as a page is read. */
const OPENED_LOCAL = "bam_mc_opened";

export function readOpenedMasterclasses() {
  try {
    const v = JSON.parse(localStorage.getItem(OPENED_LOCAL) || "{}");
    return v && typeof v === "object" && !Array.isArray(v) ? v : {};
  } catch { return {}; }
}

function markOpened(moduleKey) {
  if (!moduleKey) return;
  const all = readOpenedMasterclasses();
  if (all[moduleKey]) return;
  try { localStorage.setItem(OPENED_LOCAL, JSON.stringify({ ...all, [moduleKey]: new Date().toISOString() })); } catch { /* private window */ }
}

/** Fold one module's count into the index, locally and on the account. */
function recordProgress(moduleKey, read, total) {
  if (!moduleKey || !total) return;
  const entry = { read, total, at: new Date().toISOString() };
  const local = readProgressIndex();
  const prev = local[moduleKey];
  if (prev && prev.read === read && prev.total === total) return;
  try { localStorage.setItem(INDEX_LOCAL, JSON.stringify({ ...local, [moduleKey]: entry })); } catch { /* private window */ }
  // The account copy merges into whatever is there, so a phone writing one
  // module never drops the laptop's others.
  loadUserData(INDEX_KEY, {})
    .then(remote => {
      const base = remote && typeof remote === "object" && !Array.isArray(remote) ? remote : {};
      return saveUserData(INDEX_KEY, { ...base, [moduleKey]: entry });
    })
    .catch(() => {});
}

/**
 * Keeps a module's `visited` map in sync with the account.
 *
 * Takes the state the reader already owns rather than owning it itself, so
 * nothing downstream changes: `visited` is still the same object, still
 * backed by localStorage, and still works offline and signed out. This only
 * adds a second home for it.
 */
export function useMasterclassProgress(moduleKey, visited, setVisited, total = 0) {
  const hydrated = useRef(false);
  const timer = useRef(null);
  const lastSaved = useRef(null);

  // ── Pull once, merge in ──────────────────────────────────────────────
  useEffect(() => {
    markOpened(moduleKey);
    let alive = true;
    hydrated.current = false;
    loadUserData(keyFor(moduleKey), {}).then((remote) => {
      if (!alive) return;
      if (remote && typeof remote === "object" && !Array.isArray(remote)) {
        setVisited((local) => {
          const merged = { ...remote, ...local };
          // Only set state if something actually changed, or this triggers
          // the save effect below on every mount and writes a row nobody
          // asked for.
          const same = Object.keys(merged).length === Object.keys(local || {}).length;
          return same ? local : merged;
        });
      }
      hydrated.current = true;
    }).catch(() => { hydrated.current = true; });
    return () => { alive = false; clearTimeout(timer.current); };
  }, [moduleKey, setVisited]);

  // ── Push on change, debounced ────────────────────────────────────────
  useEffect(() => {
    // Never write before the pull has landed. Writing first would push an
    // empty local map over a real one and wipe the coach's progress, which
    // is the one outcome worse than not syncing at all.
    if (!hydrated.current) return;
    const body = JSON.stringify(visited || {});
    if (body === lastSaved.current) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      lastSaved.current = body;
      saveUserData(keyFor(moduleKey), visited || {}).catch(() => {});
      const read = Object.keys(visited || {}).filter(k => visited[k]).length;
      recordProgress(moduleKey, read, total);
    }, SAVE_DEBOUNCE_MS);
    return () => clearTimeout(timer.current);
  }, [moduleKey, visited, total]);
}

/**
 * One analytics event per page actually read.
 *
 * Fired on completion rather than on render, because a coach paging past
 * something is not a read and counting it would make the engagement numbers
 * flattering and useless. `pct` rides along so a query can find who is
 * halfway through without unpacking the visited map.
 */
export function logMasterclassPage({ moduleKey, chapterId, index, total, visitedCount }) {
  const pct = total ? Math.round((visitedCount / total) * 100) : 0;
  logEvent("masterclass_page_read", {
    module_key: moduleKey,
    chapter_id: chapterId,
    index,
    total,
    pct,
    completed: visitedCount >= total,
  });
}
